import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Label, Modal, Select, Tabs, TextInput, Textarea } from "flowbite-react";
import { toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";
import ReactSelect from "react-select";
import PartnerOpportunitiesApi from "../../Api/PartnerOpportunitiesApi";
import CommonApi from "../../Api/CommonApi";
import {
  PartnerOpportunity,
  PartnerOpportunityManufacturerMember,
  PartnerOpportunityMetadata,
  PartnerOpportunityRegionOption,
  PartnerOpportunityResponseDto,
  PartnerOpportunitySearchRequest,
  opportunityDisplayHeading,
  seekingTypeDisplayLabel,
} from "../../Models/PartnerOpportunity";
import { State } from "../../Models/State";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import { SubscriptionLevelNames } from "../../Models/Subscription";
import { solidMenuReactSelectProps } from "../../Utils/reactSelectStyles";

type TagOption = {
  label: string;
  value: string;
};

const defaultSearch: PartnerOpportunitySearchRequest = {
  keyword: "",
  seekingType: "",
  status: "",
  memberId: undefined,
  favoritesOnly: false,
  regions: [],
  states: [],
  categories: [],
  page: 1,
  pageSize: 50,
};

function opportunityCardSubtitle(item: PartnerOpportunity): string {
  const mn = (item.memberName ?? "").trim();
  const cn = (item.companyName ?? "").trim();
  if (mn && cn && cn !== mn) return cn;
  return "";
}

const blankOpportunity: PartnerOpportunity = {
  id: 0,
  memberId: 0,
  title: "",
  description: "",
  companyName: "",
  seekingType: "Both",
  status: "Unapproved",
  locationMode: "Region",
  regions: [],
  states: [],
  categories: [],
  isFavorite: false,
};

function isOwnerListing(item: PartnerOpportunity, memberId?: number | null): boolean {
  return memberId != null && item.memberId === memberId;
}

function listingStatus(item: PartnerOpportunity): string {
  return (item.status ?? "").trim();
}

function statusBadgeClass(status: string): string {
  if (status === "Published") return "bg-green-100 text-green-800";
  if (status === "Archived") return "bg-gray-200 text-gray-800";
  return "bg-amber-100 text-amber-900";
}

function isStateLocationMode(mode: string | null | undefined): boolean {
  return (mode ?? "").trim().toLowerCase() === "state";
}

function OpportunityRegionsCategoriesBlock({
  locationMode,
  regions,
  states,
  categories,
  regionDetailsByName,
  stateNameByCode,
}: {
  locationMode?: string | null;
  regions: string[];
  states: string[];
  categories: string[];
  regionDetailsByName: Map<string, PartnerOpportunityRegionOption>;
  stateNameByCode: Map<string, string>;
}) {
  const stateMode = isStateLocationMode(locationMode);
  const regionList = (regions ?? []).map((r) => r.trim()).filter(Boolean);
  const stateList = (states ?? []).map((s) => s.trim()).filter(Boolean);
  const categoryList = (categories ?? []).map((c) => c.trim()).filter(Boolean);
  return (
    <div className="mt-3 rounded-lg border border-lime-200 bg-gradient-to-br from-lime-50/90 to-teal-50/50 px-3 py-2.5 shadow-sm">
      <div className="mb-2.5 last:mb-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-lime-900 mb-1.5">
          {stateMode ? "States / Provinces" : "Regions"}
        </div>
        {stateMode ? (
          <div className="flex flex-wrap gap-1.5">
            {stateList.length > 0 ? (
              stateList.map((code) => {
                const name = stateNameByCode.get(code.toUpperCase()) ?? code;
                return (
                  <span
                    key={code}
                    className="inline-flex text-xs font-semibold px-2 py-1 rounded-md bg-white/95 border border-lime-400/80 text-lime-950 shadow-sm"
                  >
                    {name}
                  </span>
                );
              })
            ) : (
              <span className="text-xs text-gray-500 italic">None listed</span>
            )}
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {regionList.length > 0 ? (
              regionList.map((r) => {
                const details = regionDetailsByName.get(r.toLowerCase());
                return (
                  <div key={r} className="rounded-md bg-white/95 border border-lime-400/80 text-lime-950 shadow-sm overflow-hidden">
                    {details?.imageUrl ? (
                      <img src={details.imageUrl} alt={r} className="h-24 w-full object-cover" />
                    ) : null}
                    <div className="p-2">
                      <div className="text-xs font-bold">{r}</div>
                      {details?.description ? (
                        <div className="text-xs text-gray-700 mt-1 leading-snug">{details.description}</div>
                      ) : null}
                    </div>
                  </div>
                );
              })
            ) : (
              <span className="text-xs text-gray-500 italic">None listed</span>
            )}
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-teal-900 mb-1.5">Categories</div>
        <div className="flex flex-wrap gap-1.5">
          {categoryList.length > 0 ? (
            categoryList.map((c) => (
              <span
                key={c}
                className="inline-flex text-xs font-semibold px-2 py-1 rounded-md bg-white/95 border border-teal-400/80 text-teal-950 shadow-sm"
              >
                {c}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-500 italic">None listed</span>
          )}
        </div>
      </div>
    </div>
  );
}

function subscriptionNameIsManufacturer(name: string | null | undefined): boolean {
  const n = (name ?? "").trim().toLowerCase();
  return n.includes("manufacturer") || n.includes("manufacturing");
}

/** Members who can post partner opportunities (Associate subscription id 3). */
function memberCanPostPartnerOpportunities(
  subscriptionName: string | null | undefined,
  subscriptionId: number | null | undefined
): boolean {
  const sid = subscriptionId ?? undefined;
  if (sid === SubscriptionLevelNames.AssociateMember) return true;
  return subscriptionNameIsManufacturer(subscriptionName);
}

export default function PartnerOpportunitiesPage() {
  const { userModel } = useAppSelector((s) => s.user);
  const isSuperUser = userModel?.userLevelId === UserLevelNames.SuperAdmin;
  const canPostPartnerOpportunities = useMemo(
    () =>
      memberCanPostPartnerOpportunities(
        userModel?.member?.subscription?.name,
        userModel?.member?.subscriptionId ?? userModel?.member?.subscription?.id
      ),
    [
      userModel?.member?.subscription?.name,
      userModel?.member?.subscriptionId,
      userModel?.member?.subscription?.id,
    ]
  );
  const canManageMyOpportunities = isSuperUser || canPostPartnerOpportunities;

  const [metadata, setMetadata] = useState<PartnerOpportunityMetadata>();
  const [search, setSearch] = useState<PartnerOpportunitySearchRequest>(defaultSearch);
  const [items, setItems] = useState<PartnerOpportunity[]>([]);
  const [myItems, setMyItems] = useState<PartnerOpportunity[]>([]);
  const [regionDetails, setRegionDetails] = useState<PartnerOpportunityRegionOption[]>([]);
  const [catalogStates, setCatalogStates] = useState<State[]>([]);
  const [manufacturerMembers, setManufacturerMembers] = useState<PartnerOpportunityManufacturerMember[]>([]);
  const [editing, setEditing] = useState<PartnerOpportunity>(blankOpportunity);
  const [showMyOpportunityModal, setShowMyOpportunityModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpp, setDetailOpp] = useState<PartnerOpportunity | null>(null);
  const [detailResponses, setDetailResponses] = useState<PartnerOpportunityResponseDto[]>([]);
  const [replyText, setReplyText] = useState("");

  const canModerate = useMemo(() => isSuperUser, [isSuperUser]);
  const regionOptions = useMemo<TagOption[]>(() => {
    return (metadata?.regions ?? [])
      .map((r) => (r ?? "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((x) => ({ label: x, value: x }));
  }, [metadata?.regions]);
  const regionDetailsByName = useMemo(() => {
    const map = new Map<string, PartnerOpportunityRegionOption>();
    regionDetails.forEach((region) => {
      const key = (region.name ?? "").trim().toLowerCase();
      if (key) map.set(key, region);
    });
    return map;
  }, [regionDetails]);
  const stateOptions = useMemo<TagOption[]>(() => {
    return catalogStates
      .map((s) => ({
        label: `${s.name} (${s.code})`,
        value: s.code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalogStates]);
  const stateNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    catalogStates.forEach((s) => {
      if (s.code) map.set(s.code.toUpperCase(), s.name);
    });
    return map;
  }, [catalogStates]);
  const categoryOptions = useMemo<TagOption[]>(() => {
    const set = new Set<string>();
    (metadata?.categories ?? []).forEach((c) => {
      const value = (c ?? "").trim();
      if (value) set.add(value);
    });
    items.forEach((item) => item.categories.forEach((c) => c && set.add(c.trim())));
    myItems.forEach((item) => item.categories.forEach((c) => c && set.add(c.trim())));
    return [...set].sort((a, b) => a.localeCompare(b)).map((x) => ({ label: x, value: x }));
  }, [metadata?.categories, items, myItems]);

  const reloadAll = useCallback(
    async (listSearch: PartnerOpportunitySearchRequest) => {
      const [metaRes, regionRes, statesRes, listRes] = await Promise.all([
        PartnerOpportunitiesApi.getMetadata(),
        PartnerOpportunitiesApi.getRegions(),
        CommonApi.getStates(),
        PartnerOpportunitiesApi.search(listSearch),
      ]);
      setMetadata(metaRes.data);
      setRegionDetails(regionRes.data);
      setCatalogStates(statesRes.data ?? []);
      setItems(listRes.data);

      if (canManageMyOpportunities && userModel?.memberId) {
        try {
          const mineRes = await PartnerOpportunitiesApi.getMy();
          setMyItems(mineRes.data);
        } catch {
          setMyItems([]);
        }
      } else {
        setMyItems([]);
      }
    },
    [canManageMyOpportunities, userModel?.memberId]
  );

  useEffect(() => {
    reloadAll(defaultSearch).catch(() => toast.error("Unable to load opportunities."));
  }, [reloadAll]);

  useEffect(() => {
    if (!isSuperUser) {
      setSearch((s) => (s.status ? { ...s, status: "" } : s));
    }
  }, [isSuperUser]);

  useEffect(() => {
    if (!isSuperUser || !showMyOpportunityModal) {
      if (!isSuperUser) setManufacturerMembers([]);
      return;
    }

    PartnerOpportunitiesApi.getManufacturerMembers()
      .then((res) => setManufacturerMembers(res.data ?? []))
      .catch(() => {
        setManufacturerMembers([]);
        toast.error("Unable to load members for assignment.");
      });
  }, [isSuperUser, showMyOpportunityModal]);

  const openMyOpportunityModal = () => {
    setEditing({
      ...blankOpportunity,
      memberId: isSuperUser ? 0 : (userModel?.memberId ?? 0),
    });
    setShowMyOpportunityModal(true);
  };

  const openEditOpportunityModal = (opportunity: PartnerOpportunity) => {
    setEditing({
      ...opportunity,
      locationMode: isStateLocationMode(opportunity.locationMode) ? "State" : "Region",
      regions: opportunity.regions ?? [],
      states: opportunity.states ?? [],
    });
    setShowMyOpportunityModal(true);
  };

  const closeMyOpportunityModal = () => {
    setShowMyOpportunityModal(false);
  };

  const applySearch = () => {
    PartnerOpportunitiesApi.search(search)
      .then((res) => setItems(res.data))
      .catch(() => toast.error("Search failed."));
  };

  const save = async () => {
    try {
      if (isSuperUser && editing.id === 0 && (!editing.memberId || editing.memberId <= 0)) {
        toast.error("Select a member before saving.");
        return;
      }
      const mode = isStateLocationMode(editing.locationMode) ? "State" : "Region";
      if (mode === "Region" && !(editing.regions ?? []).length) {
        toast.error("Select at least one region.");
        return;
      }
      if (mode === "State" && !(editing.states ?? []).length) {
        toast.error("Select at least one state or province.");
        return;
      }
      const payload = {
        ...editing,
        locationMode: mode,
        regions: mode === "Region" ? editing.regions ?? [] : [],
        states: mode === "State" ? editing.states ?? [] : [],
        title: "",
        memberId: editing.memberId || userModel?.memberId || 0,
      };
      const res = await PartnerOpportunitiesApi.upsert(payload);
      if (!res.data.success) throw new Error(res.data.message);

      closeMyOpportunityModal();
      setEditing({ ...blankOpportunity, memberId: isSuperUser ? 0 : (userModel?.memberId ?? 0) });
      toast.success("Opportunity saved.");

      try {
        await reloadAll(search);
      } catch {
        toast.warn("Saved, but the list could not be refreshed. Click Apply Filters to reload.");
      }
    } catch (e) {
      toast.error("Unable to save opportunity.");
    }
  };

  const memberLifecycle = async (
    id: number,
    status: "Published" | "Archived",
    options?: { closeModal?: boolean }
  ) => {
    const res = await PartnerOpportunitiesApi.memberLifecycle(id, status);
    if (res.data.success) {
      toast.success(status === "Published" ? "Published." : "Archived.");
      if (options?.closeModal) {
        closeMyOpportunityModal();
        setEditing({ ...blankOpportunity, memberId: isSuperUser ? 0 : (userModel?.memberId ?? 0) });
      }
      try {
        await reloadAll(search);
      } catch {
        toast.warn("Updated, but the list could not be refreshed. Click Apply Filters to reload.");
      }
      return;
    }
    toast.error(res.data.message || "Update failed.");
  };

  const moderate = async (id: number, status: string) => {
    const res = await PartnerOpportunitiesApi.moderate(id, { status });
    if (res.data.success) {
      toast.success(`Opportunity ${status.toLowerCase()}.`);
      await reloadAll(search);
      return;
    }
    toast.error(res.data.message || "Moderation failed.");
  };

  const deleteOpportunity = async (item: PartnerOpportunity) => {
    if (!window.confirm(`Delete "${opportunityDisplayHeading(item)}"?`)) return;

    const res = await PartnerOpportunitiesApi.delete(item.id);
    if (res.data.success) {
      toast.success("Opportunity deleted.");
      await reloadAll(search);
      return;
    }
    toast.error(res.data.message || "Delete failed.");
  };

  const toggleFavorite = async (item: PartnerOpportunity) => {
    if (item.isFavorite) await PartnerOpportunitiesApi.unfavorite(item.id);
    else await PartnerOpportunitiesApi.favorite(item.id);
    await applySearch();
  };

  const canViewResponsesForOpportunity = (opp: PartnerOpportunity | null) =>
    Boolean(userModel && opp && (isSuperUser || isOwnerListing(opp, userModel.memberId)));

  const canReplyToOpportunity = (opp: PartnerOpportunity | null) =>
    Boolean(
      userModel &&
        opp &&
        listingStatus(opp) === "Published" &&
        !isOwnerListing(opp, userModel.memberId)
    );

  const openDetailModal = async (item: PartnerOpportunity) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    setDetailOpp(null);
    setDetailResponses([]);
    setReplyText("");
    try {
      const oppRes = await PartnerOpportunitiesApi.getById(item.id);
      setDetailOpp(oppRes.data);
      if (userModel?.id) {
        try {
          const respRes = await PartnerOpportunitiesApi.getResponses(item.id);
          setDetailResponses(respRes.data ?? []);
        } catch {
          setDetailResponses([]);
        }
      }
    } catch {
      toast.error("Could not load this opportunity.");
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReply = async () => {
    if (!detailOpp || !replyText.trim()) {
      toast.error("Enter a message.");
      return;
    }
    try {
      const res = await PartnerOpportunitiesApi.addResponse(detailOpp.id, replyText.trim());
      if (!res.data.success) {
        toast.error(res.data.message || "Could not send reply.");
        return;
      }
      toast.success("Your reply was sent.");
      setReplyText("");
      if (userModel?.id) {
        try {
          const r = await PartnerOpportunitiesApi.getResponses(detailOpp.id);
          setDetailResponses(r.data ?? []);
        } catch {
          /* listing restricted */
        }
      }
    } catch {
      toast.error("Could not send reply.");
    }
  };

  const renderOpportunityCard = (item: PartnerOpportunity) => {
    const cardSub = opportunityCardSubtitle(item);
    const canEditListing = isSuperUser || isOwnerListing(item, userModel?.memberId);
    return (
    <div key={item.id} className="border rounded-xl p-4 bg-white shadow-sm">
      <div className="flex justify-between gap-2 items-start">
        <button
          type="button"
          className="text-left flex-1 min-w-0 rounded-lg hover:bg-gray-50 p-1 -m-1 transition-colors focus:outline-none focus:ring-2 focus:ring-lime-600"
          onClick={() => void openDetailModal(item)}
        >
          <div className="text-xl font-bold text-gray-900">{opportunityDisplayHeading(item)}</div>
          {cardSub ? <div className="text-sm text-gray-500">{cardSub}</div> : null}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <span
              className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${statusBadgeClass(listingStatus(item))}`}
            >
              {listingStatus(item) || "Unknown"}
            </span>
            <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-900">
              Seeking: {seekingTypeDisplayLabel(item.seekingType)}
            </span>
            <span className="text-xs text-emerald-700 font-medium">Open details · reply</span>
          </div>
        </button>
        <Button size="xs" color={item.isFavorite ? "failure" : "light"} onClick={() => toggleFavorite(item)}>
          {item.isFavorite ? "Unfavorite" : "Favorite"}
        </Button>
      </div>
      <div className="text-sm mt-2 max-h-24 overflow-hidden text-ellipsis">{item.description}</div>
      <OpportunityRegionsCategoriesBlock
        locationMode={item.locationMode}
        regions={item.regions}
        states={item.states ?? []}
        categories={item.categories}
        regionDetailsByName={regionDetailsByName}
        stateNameByCode={stateNameByCode}
      />
      {canEditListing && (
        <div className="flex gap-2 mt-3">
          <Button size="xs" color="light" onClick={() => openEditOpportunityModal(item)}>
            Edit
          </Button>
          {isSuperUser && (
            <Button size="xs" color="failure" onClick={() => void deleteOpportunity(item)}>
              Delete
            </Button>
          )}
        </div>
      )}
      {canModerate && (
        <div className="flex gap-2 mt-3">
          <Button size="xs" color="success" onClick={() => moderate(item.id, "Published")}>
            Approve
          </Button>
          <Button size="xs" color="failure" onClick={() => moderate(item.id, "Unapproved")}>
            Unapprove
          </Button>
          <Button size="xs" color="warning" onClick={() => moderate(item.id, "Archived")}>
            Archive
          </Button>
        </div>
      )}
      {!isSuperUser && isOwnerListing(item, userModel?.memberId) && (
        <div className="flex gap-2 mt-3">
          {(listingStatus(item) === "Unapproved" || listingStatus(item) === "Archived") && (
            <Button size="xs" color="success" onClick={() => memberLifecycle(item.id, "Published")}>
              {listingStatus(item) === "Archived" ? "Republish" : "Publish"}
            </Button>
          )}
          {(listingStatus(item) === "Unapproved" || listingStatus(item) === "Published") && (
            <Button size="xs" color="warning" onClick={() => memberLifecycle(item.id, "Archived")}>
              Archive
            </Button>
          )}
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="w-full p-4 space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <h1 className="text-4xl font-bold text-gray-900">Partner Opportunities</h1>
        {canManageMyOpportunities && (
          <Button className="buttonMain shrink-0" onClick={openMyOpportunityModal}>
            Add Opportunity
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        <div className={isSuperUser ? "grid md:grid-cols-3 gap-3" : "grid md:grid-cols-2 gap-3"}>
        <div className="space-y-2">
          <Label htmlFor="po-filter-keyword" value="Keyword" />
          <TextInput
            id="po-filter-keyword"
            placeholder="Search company or keyword"
            value={search.keyword}
            onChange={(e) => setSearch((s) => ({ ...s, keyword: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="po-filter-seeking-type" value="Seeking type" />
          <Select
            id="po-filter-seeking-type"
            value={search.seekingType}
            onChange={(e) => setSearch((s) => ({ ...s, seekingType: e.target.value }))}
          >
            <option value="">All seeking types</option>
            {(metadata?.seekingTypes ?? []).map((v) => (
              <option key={v} value={v}>
                {seekingTypeDisplayLabel(v)}
              </option>
            ))}
          </Select>
          {search.seekingType ? (
            <p className="text-xs text-gray-600">
              Showing listings for{" "}
              <span className="font-medium text-gray-900">
                {seekingTypeDisplayLabel(search.seekingType)}
              </span>
              {search.seekingType !== "Both"
                ? " (includes opportunities seeking Distributors and Reps)"
                : null}
            </p>
          ) : null}
        </div>
        {isSuperUser && (
          <div className="space-y-2">
            <Label htmlFor="po-filter-status" value="Status" />
            <Select
              id="po-filter-status"
              value={search.status}
              onChange={(e) => setSearch((s) => ({ ...s, status: e.target.value }))}
            >
              <option value="">All statuses</option>
              {(metadata?.statuses ?? []).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label value="Regions" />
          <ReactSelect<TagOption, true>
            isMulti
            isClearable
            closeMenuOnSelect={false}
            placeholder="All regions"
            options={regionOptions}
            value={search.regions.map((r) => ({ label: r, value: r }))}
            {...solidMenuReactSelectProps}
            onChange={(selected) =>
              setSearch((s) => ({
                ...s,
                regions: selected?.map((o) => o.value).filter(Boolean) ?? [],
              }))
            }
          />
        </div>
        <div className="space-y-2">
          <Label value="States / Provinces" />
          <ReactSelect<TagOption, true>
            isMulti
            isClearable
            closeMenuOnSelect={false}
            placeholder="All states"
            options={stateOptions}
            value={search.states.map((code) => ({
              label: stateNameByCode.get(code.toUpperCase())
                ? `${stateNameByCode.get(code.toUpperCase())} (${code})`
                : code,
              value: code,
            }))}
            {...solidMenuReactSelectProps}
            onChange={(selected) =>
              setSearch((s) => ({
                ...s,
                states: selected?.map((o) => o.value).filter(Boolean) ?? [],
              }))
            }
          />
        </div>
        <div className="space-y-2">
          <Label value="Categories" />
          <ReactSelect<TagOption, true>
            isMulti
            isClearable
            closeMenuOnSelect={false}
            placeholder="All categories"
            options={categoryOptions}
            value={search.categories.map((c) => ({ label: c, value: c }))}
            {...solidMenuReactSelectProps}
            onChange={(selected) =>
              setSearch((s) => ({
                ...s,
                categories: selected?.map((o) => o.value).filter(Boolean) ?? [],
              }))
            }
          />
        </div>
      </div>
      </div>

      <Button className="buttonMain" onClick={applySearch}>Apply Filters</Button>

      {canManageMyOpportunities ? (
        <div className="w-full">
          <Tabs aria-label="Partner opportunities lists" style="underline">
            <Tabs.Item active title="Active Opportunities" className="font-bold">
              <div className="pt-2">
                {items.length === 0 ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-gray-700">
                    <p className="font-medium text-gray-900">No opportunities to show here yet.</p>
                    <p className="mt-2 text-sm leading-relaxed">
                      {isSuperUser ? (
                        <>
                          By default this list includes <strong>all</strong> statuses. Use the status filter to narrow to
                          Unapproved, Published, or Archived. If nothing appears, try clearing the search box and filters, then
                          click <strong>Apply Filters</strong>.
                        </>
                      ) : (
                        <>
                          You will see <strong>published</strong> opportunities, and if you are logged in, any opportunities
                          posted under your member account (including unapproved listings). If nothing appears, try clearing the
                          search box and filters, then click <strong>Apply Filters</strong>.
                        </>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3 lg:grid-cols-2">{items.map(renderOpportunityCard)}</div>
                )}
              </div>
            </Tabs.Item>
            <Tabs.Item title="My existing opportunities" className="font-bold">
              <div className="space-y-3 pt-2">
                {myItems.length === 0 ? (
                  <p className="text-sm text-gray-600">No listings yet. Use Add Opportunity to create one.</p>
                ) : (
                  <ul className="space-y-1">
                    {myItems.map((x) => (
                      <li key={x.id} className="flex flex-wrap gap-2 items-center">
                        <button
                          type="button"
                          className="text-left text-sm text-blue-700 hover:underline"
                          onClick={() => openEditOpportunityModal(x)}
                        >
                          {opportunityDisplayHeading(x)} ({x.status})
                        </button>
                        <button
                          type="button"
                          className="text-sm text-emerald-700 hover:underline"
                          onClick={() => void openDetailModal(x)}
                        >
                          View · responses
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Tabs.Item>
          </Tabs>
        </div>
      ) : (
        <div className="space-y-3 w-full">
          <h2 className="text-2xl font-bold">Active Opportunities</h2>
          {items.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-gray-700">
              <p className="font-medium text-gray-900">No opportunities to show here yet.</p>
              <p className="mt-2 text-sm leading-relaxed">
                {isSuperUser ? (
                  <>
                    By default this list includes <strong>all</strong> statuses. Use the status filter to narrow results. If
                    nothing appears, try clearing the search box and filters, then click <strong>Apply Filters</strong>.
                  </>
                ) : (
                  <>
                    You will see <strong>published</strong> opportunities, and if you are logged in, any opportunities posted
                    under your member account (including unapproved listings). If nothing appears, try clearing the search box
                    and filters, then click <strong>Apply Filters</strong>.
                  </>
                )}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">{items.map(renderOpportunityCard)}</div>
          )}
        </div>
      )}

      <Modal show={showDetailModal} onClose={() => setShowDetailModal(false)} size="xl">
        <Modal.Header>Opportunity details</Modal.Header>
        <Modal.Body>
          {detailLoading && <p className="text-sm text-gray-600">Loading…</p>}
          {!detailLoading && detailOpp && (() => {
            const detailSub = opportunityCardSubtitle(detailOpp);
            return (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{opportunityDisplayHeading(detailOpp)}</h3>
                {detailSub ? <p className="text-sm text-gray-600">{detailSub}</p> : null}
                <p className="text-xs text-gray-500 mt-1">Status: {detailOpp.status}</p>
                <p className="text-sm text-gray-700 mt-1">
                  <span className="font-semibold">Seeking type:</span>{" "}
                  {seekingTypeDisplayLabel(detailOpp.seekingType)}
                </p>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{detailOpp.description}</p>
              <OpportunityRegionsCategoriesBlock
                locationMode={detailOpp.locationMode}
                regions={detailOpp.regions}
                states={detailOpp.states ?? []}
                categories={detailOpp.categories}
                regionDetailsByName={regionDetailsByName}
                stateNameByCode={stateNameByCode}
              />

              {canViewResponsesForOpportunity(detailOpp) && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Responses</h4>
                  {detailResponses.length === 0 ? (
                    <p className="text-sm text-gray-600">No responses yet.</p>
                  ) : (
                    <ul className="space-y-3">
                      {detailResponses.map((r) => (
                        <li key={r.id} className="border rounded-lg p-3 text-sm bg-gray-50">
                          <div className="text-xs text-gray-500 mb-1">
                            {new Date(r.dateCreated).toLocaleString()} — {r.responder.firstName} {r.responder.lastName}
                          </div>
                          <p className="whitespace-pre-wrap text-gray-800">{r.message}</p>
                          <div className="text-xs text-gray-600 mt-2 space-y-0.5">
                            <div>Email: {r.responder.emailAddress || "—"}</div>
                            <div>Phone: {r.responder.phoneNumber || "—"}</div>
                            {r.responder.memberName && (
                              <div>
                                Their organization: {r.responder.memberName}
                                {(r.responder.memberCity || r.responder.memberState) &&
                                  ` · ${r.responder.memberCity ?? ""}${r.responder.memberCity && r.responder.memberState ? ", " : ""}${r.responder.memberState ?? ""}`}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {canReplyToOpportunity(detailOpp) ? (
                <div className="border-t border-gray-200 pt-4">
                  <Label htmlFor="po-reply" value="Your reply" />
                  <Textarea
                    id="po-reply"
                    rows={4}
                    className="mt-1"
                    placeholder="Introduce yourself and explain your interest…"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <Button className="buttonMain mt-2" onClick={() => void submitReply()}>
                    Send reply
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Member admins for this listing are notified by email with your profile and message.
                  </p>
                </div>
              ) : !userModel && listingStatus(detailOpp) === "Published" ? (
                <p className="text-sm text-gray-600 border-t border-gray-200 pt-4">Log in to reply to this opportunity.</p>
              ) : userModel && listingStatus(detailOpp) === "Published" && isOwnerListing(detailOpp, userModel.memberId) ? (
                <p className="text-sm text-gray-600 border-t border-gray-200 pt-4">
                  Replies from other members appear above when available.
                </p>
              ) : null}
            </div>
            );
          })()}
        </Modal.Body>
      </Modal>

      <Modal show={showMyOpportunityModal} onClose={closeMyOpportunityModal} size="xl">
        <Modal.Header>My opportunity</Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button
                size="xs"
                color="light"
                onClick={() =>
                  setEditing({ ...blankOpportunity, memberId: isSuperUser ? 0 : (userModel?.memberId ?? 0) })
                }
              >
                New opportunity
              </Button>
            </div>
            {isSuperUser && editing.id === 0 && (
              <>
                <Label htmlFor="po-memberId" value="Member" />
                <Select
                  id="po-memberId"
                  value={editing.memberId || 0}
                  onChange={(e) => setEditing((s) => ({ ...s, memberId: Number(e.target.value) }))}
                >
                  <option value={0}>Select member</option>
                  {manufacturerMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </>
            )}
            <Label htmlFor="po-seekingType" value="Seeking Type" />
            <Select
              id="po-seekingType"
              value={editing.seekingType ?? "Both"}
              onChange={(e) => setEditing((s) => ({ ...s, seekingType: e.target.value }))}
            >
              <option value="Distributor">Distributor</option>
              <option value="Rep">Rep</option>
              <option value="Both">Distributors and Reps</option>
            </Select>
            <p className="text-xs text-gray-500">
              The listing headline is generated from the posting member&apos;s name and seeking type.
            </p>
            <Label htmlFor="po-description" value="Description" />
            <Textarea
              id="po-description"
              rows={4}
              value={editing.description ?? ""}
              onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
            />
            <Label value="Location mode" />
            <div className="flex flex-wrap gap-2">
              <Button
                size="xs"
                color={!isStateLocationMode(editing.locationMode) ? "success" : "light"}
                onClick={() =>
                  setEditing((s) => ({
                    ...s,
                    locationMode: "Region",
                    states: [],
                  }))
                }
              >
                Regions
              </Button>
              <Button
                size="xs"
                color={isStateLocationMode(editing.locationMode) ? "success" : "light"}
                onClick={() =>
                  setEditing((s) => ({
                    ...s,
                    locationMode: "State",
                    regions: [],
                  }))
                }
              >
                States / Provinces
              </Button>
            </div>
            {!isStateLocationMode(editing.locationMode) ? (
              <>
                <Label htmlFor="po-regions" value="Regions" />
                <ReactSelect
                  inputId="po-regions"
                  placeholder="Select regions..."
                  isClearable
                  isMulti
                  value={(editing.regions ?? []).map((r) => ({ label: r, value: r }))}
                  {...solidMenuReactSelectProps}
                  onChange={(selected) => {
                    const next = selected?.map((s) => s.value.trim()).filter(Boolean) ?? [];
                    setEditing((s) => ({ ...s, regions: next }));
                  }}
                  options={regionOptions}
                />
              </>
            ) : (
              <>
                <Label htmlFor="po-states" value="States / Provinces" />
                <ReactSelect
                  inputId="po-states"
                  placeholder="Select states or provinces..."
                  isClearable
                  isMulti
                  value={(editing.states ?? []).map((code) => ({
                    label: stateNameByCode.get(code.toUpperCase())
                      ? `${stateNameByCode.get(code.toUpperCase())} (${code})`
                      : code,
                    value: code,
                  }))}
                  {...solidMenuReactSelectProps}
                  onChange={(selected) => {
                    const next = selected?.map((s) => s.value.trim()).filter(Boolean) ?? [];
                    setEditing((s) => ({ ...s, states: next }));
                  }}
                  options={stateOptions}
                />
              </>
            )}
            <Label htmlFor="po-categories" value="Categories" />
            <CreatableSelect
              inputId="po-categories"
              placeholder="Add categories..."
              isClearable
              isMulti
              value={(editing.categories ?? []).map((c) => ({ label: c, value: c }))}
              {...solidMenuReactSelectProps}
              onChange={(selected) => {
                const next = selected?.map((s) => s.label.trim()).filter(Boolean) ?? [];
                setEditing((s) => ({ ...s, categories: next }));
              }}
              options={categoryOptions}
            />
            <div className="flex flex-wrap gap-2">
              <Button className="buttonMain" onClick={save}>
                Save Opportunity
              </Button>
              {editing.id > 0 &&
                !isSuperUser &&
                isOwnerListing(editing, userModel?.memberId) &&
                (listingStatus(editing) === "Unapproved" || listingStatus(editing) === "Archived") && (
                  <Button
                    color="success"
                    onClick={() => memberLifecycle(editing.id, "Published", { closeModal: true })}
                  >
                    {listingStatus(editing) === "Archived" ? "Republish" : "Publish"}
                  </Button>
                )}
              {editing.id > 0 &&
                !isSuperUser &&
                isOwnerListing(editing, userModel?.memberId) &&
                (listingStatus(editing) === "Unapproved" || listingStatus(editing) === "Published") && (
                  <Button
                    color="warning"
                    onClick={() => memberLifecycle(editing.id, "Archived", { closeModal: true })}
                  >
                    Archive
                  </Button>
                )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
